import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type { PainelFinanceiroFilters } from '../types';

export const selectFinanceiroEscritorioState = (state: RootState) => state.financeiroEscritorio;

export const selectPainelResumo = (state: RootState) => state.financeiroEscritorio.resumo;
export const selectPainelLoading = (state: RootState) => state.financeiroEscritorio.loading;
export const selectPainelLoadingDetalhe = (state: RootState) =>
  state.financeiroEscritorio.loadingDetalhe;
export const selectPainelSaving = (state: RootState) => state.financeiroEscritorio.saving;
export const selectPainelError = (state: RootState) => state.financeiroEscritorio.error;
export const selectFaturasEscritorio = (state: RootState) => state.financeiroEscritorio.faturas;
export const selectFaturaEscritorioAtual = (state: RootState) =>
  state.financeiroEscritorio.faturaAtual;
export const selectPainelFilters = (state: RootState) => state.financeiroEscritorio.filters;
export const selectBannerDispensadoNaSessao = (state: RootState) =>
  state.financeiroEscritorio.bannerDispensadoNaSessao;
export const selectAlertaFinanceiroGlobal = (state: RootState) =>
  state.financeiroEscritorio.resumo?.alertaGlobal ?? null;

const aplicarFiltros = (
  faturas: ReturnType<typeof selectFaturasEscritorio>,
  filters: PainelFinanceiroFilters,
) => {
  return faturas.filter((fatura) => {
    if (filters.status !== 'TODOS' && fatura.status !== filters.status) {
      return false;
    }
    if (filters.ano !== null) {
      const anoFatura = new Date(fatura.dataEmissao).getFullYear();
      if (anoFatura !== filters.ano) return false;
    }
    if (filters.busca.trim()) {
      const busca = filters.busca.trim().toLowerCase();
      if (!fatura.numero.toLowerCase().includes(busca)) return false;
    }
    return true;
  });
};

export const selectFaturasFiltradas = createSelector(
  [selectFaturasEscritorio, selectPainelFilters],
  (faturas, filters) => aplicarFiltros(faturas, filters),
);

export const selectAnosDisponiveis = createSelector([selectFaturasEscritorio], (faturas) => {
  const anos = new Set<number>();
  faturas.forEach((f) => anos.add(new Date(f.dataEmissao).getFullYear()));
  return Array.from(anos).sort((a, b) => b - a);
});

export const selectConsumoEmAberto = (state: RootState) =>
  state.financeiroEscritorio.consumoEmAberto;

export const selectConsumoEmAbertoLoading = (state: RootState) =>
  state.financeiroEscritorio.consumoEmAbertoLoading;

export const selectConsumoEmAbertoCategorias = createSelector([selectConsumoEmAberto], (itens) => {
  const mapa = new Map<string, string>();
  itens.forEach((item) => {
    mapa.set(item.categoria, item.servicoLabel);
  });
  return Array.from(mapa.entries()).map(([value, label]) => ({ value, label }));
});

export const selectEstornos = (state: RootState) => state.financeiroEscritorio.estornos;
export const selectEstornosLoading = (state: RootState) =>
  state.financeiroEscritorio.estornosLoading;
export const selectEstornosFilters = (state: RootState) =>
  state.financeiroEscritorio.estornosFilters;

export const selectEstornosFiltrados = createSelector(
  [selectEstornos, selectEstornosFilters],
  (estornos, filters) => {
    const termo = filters.busca.trim().toLowerCase();
    return estornos.filter((estorno) => {
      if (filters.status !== 'TODOS' && estorno.status !== filters.status) return false;
      if (filters.ano !== null) {
        const ano = new Date(estorno.solicitadoEm).getFullYear();
        if (ano !== filters.ano) return false;
      }
      if (!termo) return true;
      return (
        estorno.faturaNumero.toLowerCase().includes(termo) ||
        estorno.motivo.toLowerCase().includes(termo) ||
        (estorno.motivoDetalhado ?? '').toLowerCase().includes(termo) ||
        estorno.solicitadoPor.toLowerCase().includes(termo)
      );
    });
  },
);

export const selectEstornosAnosDisponiveis = createSelector([selectEstornos], (estornos) => {
  const anos = new Set<number>();
  estornos.forEach((e) => anos.add(new Date(e.solicitadoEm).getFullYear()));
  return Array.from(anos).sort((a, b) => b - a);
});

export const selectEstornosStats = createSelector([selectEstornos], (estornos) => {
  const totalEstornado = estornos
    .filter((e) => e.status === 'CONCLUIDO')
    .reduce((sum, e) => sum + e.valorEstornado, 0);
  const emProcessamento = estornos.filter(
    (e) => e.status === 'EM_PROCESSAMENTO' || e.status === 'SOLICITADO',
  );
  const totalEmProcessamento = emProcessamento.reduce((sum, e) => sum + e.valorEstornado, 0);
  return {
    totalEstornado,
    quantidadeConcluidos: estornos.filter((e) => e.status === 'CONCLUIDO').length,
    quantidadeEmProcessamento: emProcessamento.length,
    totalEmProcessamento,
    quantidadeParciais: estornos.filter((e) => e.ehEstornoParcial).length,
  };
});
